import { useEffect, useState } from 'react';
import client from '../api/client';
import Layout from '../components/Layout';
import './FloorAudit.css';

const STATUS_CONFIG = {
  in_progress: { label: 'In Progress', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
  completed:   { label: 'Completed',   color: '#22c55e', bg: 'rgba(34,197,94,0.1)' },
  cancelled:   { label: 'Cancelled',   color: '#6b7280', bg: 'rgba(107,114,128,0.1)' },
};

export default function FloorAudit() {
  const [locations, setLocations]     = useState([]);
  const [activeSessions, setActive]   = useState([]);
  const [session, setSession]         = useState(null);
  const [items, setItems]             = useState([]);
  const [diff, setDiff]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [step, setStep]               = useState('select'); // select | audit | review | done
  const [selectedLoc, setSelectedLoc] = useState(null);
  const [starting, setStarting]       = useState(false);
  const [completing, setCompleting]   = useState(false);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [locsRes, activeRes] = await Promise.all([
        client.get('/locations'),
        client.get('/floor-audits/active'),
      ]);
      setLocations(locsRes.data);
      setActive(activeRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const startAudit = async (locId) => {
    setStarting(true);
    setError('');
    try {
      const res = await client.post('/floor-audits', { location_id: locId });
      setSession(res.data);
      await loadSessionItems(res.data.id);
      setStep('audit');
    } catch (err) {
      if (err.response?.status === 409) {
        setError('An audit is already in progress for this location.');
      } else {
        setError(err.response?.data?.error || 'Failed to start audit');
      }
    } finally {
      setStarting(false);
    }
  };

  const resumeAudit = async (sess) => {
    setSession(sess);
    await loadSessionItems(sess.id);
    setStep('audit');
  };

  const loadSessionItems = async (sessionId) => {
    const res = await client.get(`/floor-audits/${sessionId}/items`);
    setItems(res.data.items || []);
    setSession(res.data.session);
  };

  const updateItem = async (sessionId, itemId, field, value) => {
    try {
      const payload = field === 'verified'
        ? { verified: value }
        : { counted_quantity: parseInt(value) || 0 };
      await client.patch(`/floor-audits/${sessionId}/items/${itemId}`, payload);
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, ...payload } : i));
    } catch (err) { console.error(err); }
  };

  const reviewAudit = async () => {
    try {
      const res = await client.get(`/floor-audits/${session.id}/diff`);
      setDiff(res.data);
      setStep('review');
    } catch (err) {
      setError('Failed to load review');
    }
  };

  const completeAudit = async () => {
    setCompleting(true);
    try {
      await client.post(`/floor-audits/${session.id}/complete`);
      setStep('done');
      setSuccess(`Audit complete. ${diff?.diffs?.length || 0} inventory item${diff?.diffs?.length !== 1 ? 's' : ''} updated.`);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to complete audit');
    } finally {
      setCompleting(false);
    }
  };

  const cancelAudit = async () => {
    if (!window.confirm('Cancel this audit? No changes will be saved.')) return;
    try {
      await client.post(`/floor-audits/${session.id}/cancel`);
      setStep('select');
      setSession(null);
      setItems([]);
      loadData();
    } catch (err) { console.error(err); }
  };

  const locName = (id) => locations.find(l => l.id === id)?.name || 'Unknown';

  if (loading) return <Layout><div style={{color:'#6b7280',padding:'20px'}}>Loading...</div></Layout>;

  return (
    <Layout>
      <div className="fa-header">
        <div>
          <h1 className="fa-title">Floor Audit</h1>
          <p className="fa-sub">Audit inventory by location. Count items and apply corrections.</p>
        </div>
      </div>

      {error   && <div className="fa-error">{error}</div>}
      {success && <div className="fa-success">{success}</div>}

      {/* STEP: SELECT LOCATION */}
      {step === 'select' && (
        <div>
          {activeSessions.length > 0 && (
            <div className="fa-section">
              <div className="fa-section-title">In Progress</div>
              {activeSessions.map(s => (
                <div key={s.id} className="fa-session-card active" onClick={() => resumeAudit(s)}>
                  <div className="fa-session-left">
                    <div className="fa-session-loc">{locName(s.location_id)}</div>
                    <div className="fa-session-time">Started {new Date(s.created_at).toLocaleString()}</div>
                  </div>
                  <div className="fa-session-badge" style={STATUS_CONFIG.in_progress}>
                    Resume Audit →
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="fa-section">
            <div className="fa-section-title">Start New Audit</div>
            <p className="fa-hint">Select a location to begin auditing its inventory.</p>
            {locations.length === 0 && (
              <div className="fa-empty">No locations found. Add locations first.</div>
            )}
            <div className="fa-loc-grid">
              {locations.map(loc => {
                const hasActive = activeSessions.some(s => s.location_id === loc.id);
                return (
                  <div
                    key={loc.id}
                    className={`fa-loc-card ${hasActive ? 'fa-loc-busy' : ''} ${selectedLoc === loc.id ? 'fa-loc-selected' : ''}`}
                    onClick={() => !hasActive && setSelectedLoc(loc.id)}
                  >
                    <div className="fa-loc-name">{loc.name}</div>
                    <div className="fa-loc-type">{loc.location_type}</div>
                    {hasActive && <div className="fa-loc-busy-badge">Audit in progress</div>}
                  </div>
                );
              })}
            </div>
            {selectedLoc && (
              <button
                className="fa-btn-start"
                onClick={() => startAudit(selectedLoc)}
                disabled={starting}
              >
                {starting ? 'Starting...' : `Start Audit — ${locName(selectedLoc)}`}
              </button>
            )}
          </div>
        </div>
      )}

      {/* STEP: AUDIT */}
      {step === 'audit' && session && (
        <div>
          <div className="fa-audit-header">
            <div>
              <div className="fa-audit-loc">{locName(session.location_id)}</div>
              <div className="fa-audit-sub">{items.length} item{items.length !== 1 ? 's' : ''} to audit</div>
            </div>
            <div className="fa-audit-actions">
              <button className="fa-btn-cancel" onClick={cancelAudit}>Cancel</button>
              <button className="fa-btn-review" onClick={reviewAudit}>
                Review & Complete →
              </button>
            </div>
          </div>

          {items.length === 0 && (
            <div className="fa-empty">No inventory items at this location. You can still complete the audit.</div>
          )}

          <div className="fa-items-list">
            {items.map(item => {
              const hasDiff = item.counted_quantity !== null && item.counted_quantity !== item.system_quantity;
              return (
                <div key={item.id} className={`fa-item-row ${item.verified ? 'fa-item-verified' : ''} ${hasDiff ? 'fa-item-diff' : ''}`}>
                  <div className="fa-item-left">
                    <div className="fa-item-name">{item.item_name}</div>
                    {item.sku && <div className="fa-item-sku">SKU: {item.sku}</div>}
                    <div className="fa-item-system">System: {item.system_quantity ?? '--'}</div>
                  </div>
                  <div className="fa-item-right">
                    <div className="fa-item-count-wrap">
                      <label>Counted</label>
                      <input
                        type="number"
                        className="fa-item-input"
                        placeholder="0"
                        defaultValue={item.counted_quantity ?? ''}
                        onBlur={e => updateItem(session.id, item.id, 'counted_quantity', e.target.value)}
                      />
                    </div>
                    {hasDiff && (
                      <div className="fa-item-diff-badge">
                        {item.counted_quantity > item.system_quantity ? '+' : ''}
                        {item.counted_quantity - item.system_quantity}
                      </div>
                    )}
                    <button
                      className={`fa-item-verify ${item.verified ? 'verified' : ''}`}
                      onClick={() => updateItem(session.id, item.id, 'verified', !item.verified)}
                    >
                      {item.verified ? '✓ Verified' : 'Verify'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="fa-audit-footer">
            <button className="fa-btn-review" onClick={reviewAudit}>
              Review & Complete →
            </button>
          </div>
        </div>
      )}

      {/* STEP: REVIEW */}
      {step === 'review' && diff && (
        <div>
          <div className="fa-audit-header">
            <div>
              <div className="fa-audit-loc">Review — {diff.session?.location_name || locName(session?.location_id)}</div>
              <div className="fa-audit-sub">{diff.diffs?.length || 0} discrepanc{diff.diffs?.length !== 1 ? 'ies' : 'y'} found</div>
            </div>
            <div className="fa-audit-actions">
              <button className="fa-btn-cancel" onClick={() => setStep('audit')}>Back to Audit</button>
              <button className="fa-btn-start" onClick={completeAudit} disabled={completing}>
                {completing ? 'Applying...' : 'Apply Changes & Complete'}
              </button>
            </div>
          </div>

          {diff.diffs?.length === 0 ? (
            <div className="fa-review-clean">
              No discrepancies found. All counted quantities match the system.
              <button className="fa-btn-start" style={{marginTop:'20px'}} onClick={completeAudit} disabled={completing}>
                {completing ? 'Completing...' : 'Complete Audit'}
              </button>
            </div>
          ) : (
            <div className="fa-items-list">
              {diff.diffs.map(item => (
                <div key={item.id} className="fa-item-row fa-item-diff">
                  <div className="fa-item-left">
                    <div className="fa-item-name">{item.item_name}</div>
                    {item.sku && <div className="fa-item-sku">SKU: {item.sku}</div>}
                  </div>
                  <div className="fa-item-right">
                    <div className="fa-review-qty">
                      <span className="fa-review-label">System</span>
                      <span className="fa-review-val">{item.system_quantity}</span>
                    </div>
                    <div className="fa-review-arrow">→</div>
                    <div className="fa-review-qty">
                      <span className="fa-review-label">Counted</span>
                      <span className="fa-review-val" style={{color: item.counted_quantity > item.system_quantity ? '#22c55e' : '#ef4444'}}>
                        {item.counted_quantity}
                      </span>
                    </div>
                    <div className={`fa-item-diff-badge ${item.counted_quantity > item.system_quantity ? 'positive' : 'negative'}`}>
                      {item.counted_quantity > item.system_quantity ? '+' : ''}
                      {item.counted_quantity - item.system_quantity}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* STEP: DONE */}
      {step === 'done' && (
        <div className="fa-done">
          <div className="fa-done-check">✓</div>
          <div className="fa-done-title">Audit Complete</div>
          <div className="fa-done-sub">{success}</div>
          <button className="fa-btn-start" onClick={() => { setStep('select'); setSession(null); setItems([]); setDiff(null); setSuccess(''); }}>
            Start Another Audit
          </button>
        </div>
      )}
    </Layout>
  );
}